import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useAutoSave } from '@/hooks/useAutoSave';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { parseDoseCombination } from './dose-utils';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Loader2, Save, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import type { ClinicianResponse } from './assessment/types';
import { SaveStateChip } from './assessment/SaveStateChip';
import { DoseCombobox } from './assessment/DoseCombobox';
import { useAssessmentSubmit } from './assessment/useAssessmentSubmit';

interface ClinicalData {
  tsh: number | null;
  weight: number | null;
  age: number | null;
  on_treatment: boolean;
  previous_dose: number | null;
  thyroidectomy: boolean;
  hypothyroidism_before: boolean;
  miscarriage_history: boolean;
  planning_ivf: boolean;
  fT4: number | null;
  TAI: boolean;
  tai_handling_choice_naive_unknown: string | null;
  optional_treatment_choice: string | null;
  medications: {
    iron_supplements: boolean;
    calcium_supplements: boolean;
  };
}
export const PreconceptionAssessment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [startTime] = useState(Date.now());
  const { submit, submitting, submitPhase } = useAssessmentSubmit<ClinicalData>({
    arm: 'preconception',
    tables: {
      cases: 'preconception_cases',
      clinicianResponses: 'preconception_clinician_responses',
      apiResponses: 'preconception_api_responses',
      concordanceResults: 'preconception_concordance_results',
    },
    railwayUrl: import.meta.env.VITE_RAILWAY_PRECONCEPTION_URL as string,
    payloadKey: 'patient',
    buildCaseInsert: (d) => ({
      tsh: d.tsh,
      weight: d.weight,
      age: d.age,
      on_treatment: d.on_treatment,
      previous_dose: d.previous_dose,
      thyroidectomy: d.thyroidectomy,
      hypothyroidism_before: d.hypothyroidism_before,
      miscarriage_history: d.miscarriage_history,
      planning_ivf: d.planning_ivf,
      ft4: d.fT4,
      tai_status: d.TAI,
      tai_handling_choice_naive_unknown: d.tai_handling_choice_naive_unknown,
      optional_treatment_choice: d.optional_treatment_choice,
      iron_supplements: d.medications.iron_supplements,
      calcium_supplements: d.medications.calcium_supplements,
    }),
    buildRailwayPayload: (d) => ({
      tsh: d.tsh,
      weight: d.weight,
      age: d.age,
      on_treatment: d.on_treatment,
      thyroidectomy: d.thyroidectomy,
      previous_dose: d.previous_dose,
      fT4: d.fT4,
      TAI: d.TAI,
      planning_ivf: d.planning_ivf,
      miscarriage_history: d.miscarriage_history,
      medications: {
        iron_supplements: d.medications.iron_supplements,
        calcium_supplements: d.medications.calcium_supplements,
      },
      consent_given: true,
    }),
    buildApiResponseExtra: (railwayData) => ({ strategy_used: railwayData.strategy_used }),
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [clinicalData, setClinicalData] = useState<ClinicalData>({
    tsh: null,
    weight: null,
    age: null,
    on_treatment: false,
    previous_dose: null,
    thyroidectomy: false,
    hypothyroidism_before: false,
    miscarriage_history: false,
    planning_ivf: false,
    fT4: null,
    TAI: false,
    tai_handling_choice_naive_unknown: null,
    optional_treatment_choice: null,
    medications: {
      iron_supplements: false,
      calcium_supplements: false,
    },
  });

  const [clinicianResponse, setClinicianResponse] = useState<ClinicianResponse>({
    recommended_dose: '',
    treatment_recommendation: '',
    previous_dose: '',
    notes: '',
  });

  // Auto-save functionality
  const combinedFormData = { clinicalData, clinicianResponse };
  const { loadFromStorage, clearSavedData, getSavedDataAge, lastSavedAt } = useAutoSave({
    key: `preconception_assessment_${user?.id}`,
    data: combinedFormData,
    delay: 8000, // Increased from 3 seconds to 8 seconds - less frequent saves
    enabled: !submitting && hasUnsavedChanges,
  });

  // Load saved data on component mount
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && savedData.clinicalData && savedData.clinicianResponse) {
      const savedAge = getSavedDataAge();
      if (savedAge) {
        // Only show notification if data is substantial (not just empty form)
        const hasSubstantialData =
          (savedData.clinicalData.tsh ?? 0) > 0 ||
          (savedData.clinicalData.weight ?? 0) > 0 ||
          (savedData.clinicalData.age ?? 0) > 0 ||
          savedData.clinicianResponse.recommended_dose !== '' ||
          savedData.clinicianResponse.notes !== '';
          
        if (hasSubstantialData) {
          toast({
            title: 'Βρέθηκαν αποθηκευμένα δεδομένα',
            description: `Τελευταία αποθήκευση: ${savedAge.toLocaleString('el-GR')}`,
            duration: 4000,
          });
        }
        
        setClinicalData(savedData.clinicalData);
        setClinicianResponse(savedData.clinicianResponse);
      }
    }
  }, []);

  // Track unsaved changes - ONLY if form has meaningful data
  useEffect(() => {
    // Check if form has ANY meaningful data
    const hasSubstantialData =
      (clinicalData.tsh ?? 0) > 0 ||
      (clinicalData.weight ?? 0) > 0 ||
      (clinicalData.age ?? 0) > 0 ||
      clinicalData.on_treatment ||
      clinicalData.thyroidectomy ||
      clinicalData.TAI ||
      clinicalData.planning_ivf ||
      clinicalData.miscarriage_history ||
      clinicalData.fT4 !== null ||
      clinicianResponse.recommended_dose !== '' ||
      clinicianResponse.treatment_recommendation !== '' ||
      clinicianResponse.notes !== '';
    
    // Only set unsaved changes if there's actual data AND we're not submitting
    if (hasSubstantialData && !submitting) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [clinicalData, clinicianResponse, submitting]);

  useUnsavedChangesGuard(hasUnsavedChanges);

  // Form validation. Required numeric fields are null when empty.
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (clinicalData.tsh === null) {
      errors.tsh = 'Η TSH είναι υποχρεωτική';
    } else if (clinicalData.tsh < 0 || clinicalData.tsh > 100) {
      errors.tsh = 'Η TSH πρέπει να είναι μεταξύ 0-100';
    }

    if (clinicalData.weight === null) {
      errors.weight = 'Το βάρος είναι υποχρεωτικό';
    } else if (clinicalData.weight < 30 || clinicalData.weight > 150) {
      errors.weight = 'Το βάρος πρέπει να είναι μεταξύ 30-150 kg';
    }

    if (clinicalData.age === null) {
      errors.age = 'Η ηλικία είναι υποχρεωτική';
    } else if (clinicalData.age < 18 || clinicalData.age > 50) {
      errors.age = 'Η ηλικία πρέπει να είναι μεταξύ 18-50 ετών';
    }
    
    if (clinicalData.on_treatment && !clinicalData.previous_dose) {
      errors.previous_dose = 'Η προηγούμενη δόση είναι υποχρεωτική όταν η ασθενής λαμβάνει θεραπεία';
    }
    
    if (!clinicianResponse.recommended_dose) {
      errors.recommended_dose = 'Η προτεινόμενη δοσολογία είναι υποχρεωτική';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!validateForm()) {
      toast({
        variant: 'destructive',
        title: 'Σφάλματα φόρμας',
        description: 'Παρακαλώ διορθώστε τα σφάλματα πριν συνεχίσετε.',
      });
      return;
    }

    await submit(clinicalData, clinicianResponse, {
      startTime,
      clearDraft: () => {
        clearSavedData();
        setHasUnsavedChanges(false);
      },
    });
  };

  // Helper function to get field error
  const getFieldError = (fieldName: string) => formErrors[fieldName];
  
  // Helper function to check if field has error
  const hasFieldError = (fieldName: string) => !!formErrors[fieldName];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f7f2' }}>
      <Card className="w-full max-w-4xl mx-auto shadow-lg">
        <CardHeader style={{ backgroundColor: '#D4D8C7' }} className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-800">
                Φόρμα εκτίμησης Ειδικού Ενδοκρινολόγου - Preconception
              </CardTitle>
              <p className="text-sm text-gray-800 mt-1">
                Κλινική μελέτη συμφωνίας αποφάσεων πριν την κύηση
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {hasUnsavedChanges && !submitting && (
                <Badge variant="outline" className="text-orange-600 border-orange-200">
                  <Clock className="w-3 h-3 mr-1" />
                  Μη αποθηκευμένα
                </Badge>
              )}
              {submitting && (
                <Badge variant="outline" className="text-blue-600 border-blue-200">
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Επεξεργασία...
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      <CardContent>
        {/* Error Summary */}
        {Object.keys(formErrors).length > 0 && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-medium mb-2">Παρακαλώ διορθώστε τα παρακάτω σφάλματα:</div>
              <ul className="list-disc list-inside space-y-1">
                {Object.entries(formErrors).map(([field, error]) => (
                  <li key={field} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Required Fields */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold text-white shrink-0" style={{ backgroundColor: 'var(--ink)' }}>1</span>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Υποχρεωτικά Πεδία</h3>
            </div>
            
            {/* TSH - Full Width, Bigger Text */}
            <div className="space-y-2 mb-6">
              <Label htmlFor="tsh" className="text-base font-medium">
                TSH (0-100) <span className="text-red-500">*</span>
              </Label>
              <DecimalInput
                id="tsh"
                value={clinicalData.tsh}
                onValueChange={(v) => setClinicalData({ ...clinicalData, tsh: v })}
                placeholder="για παράδειγμα, 3.2 ή 3,2"
                className={`text-lg h-12 ${hasFieldError('tsh') ? 'border-red-500 focus:border-red-500' : ''}`}
                required
              />
              {hasFieldError('tsh') && (
                <div className="flex items-center space-x-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{getFieldError('tsh')}</span>
                </div>
              )}
            </div>
            
            {/* Weight, Age, fT4 - Same Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="weight" className="text-sm font-medium">
                  Βάρος kg (30-150) <span className="text-red-500">*</span>
                </Label>
                <DecimalInput
                  id="weight"
                  value={clinicalData.weight}
                  onValueChange={(v) => setClinicalData({ ...clinicalData, weight: v })}
                  placeholder="για παράδειγμα, 65.5 ή 65,5"
                  className={hasFieldError('weight') ? 'border-red-500 focus:border-red-500 transition-colors' : 'transition-colors'}
                  required
                />
                {hasFieldError('weight') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('weight')}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="age" className="text-sm font-medium">
                  Ηλικία (18-50) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="50"
                  value={clinicalData.age ?? ''}
                  onChange={(e) => setClinicalData({
                    ...clinicalData,
                    age: e.target.value === '' ? null : parseInt(e.target.value),
                  })}
                  className={`font-mono tabular-nums ${hasFieldError('age') ? 'border-red-500 focus:border-red-500 transition-colors' : 'transition-colors'}`}
                  required
                />
                {hasFieldError('age') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('age')}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ft4" className="text-sm font-medium">fT4</Label>
                <DecimalInput
                  id="ft4"
                  value={clinicalData.fT4}
                  onValueChange={(v) => setClinicalData({ ...clinicalData, fT4: v })}
                  placeholder="για παράδειγμα, 1.2 ή 1,2"
                />
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="on_treatment"
                checked={clinicalData.on_treatment}
                onCheckedChange={(checked) => {
                  const isOnTreatment = !!checked;
                  setClinicalData({
                    ...clinicalData, 
                    on_treatment: isOnTreatment,
                    // Restore original logic for hypothyroidism_before
                    hypothyroidism_before: isOnTreatment ? true : clinicalData.hypothyroidism_before,
                    // Add correct logic to reset previous_dose if unchecked
                    previous_dose: isOnTreatment ? clinicalData.previous_dose : null
                  });
                   // Also reset the UI dropdown if unchecked
                  if (!isOnTreatment) {
                      setClinicianResponse(prev => ({ ...prev, previous_dose: '' }));
                  }
                }}
              />
              <Label htmlFor="on_treatment" className="text-red-600">Ήδη υπό αγωγή με λεβοθυροξίνη</Label>
            </div>

            {clinicalData.on_treatment && (
              <div className="space-y-2">
                <Label htmlFor="previous_dose" className="text-sm font-medium">
                  Προηγούμενη Δόση - Previous Dose <span className="text-red-500">*</span>
                </Label>
                <DoseCombobox
                  value={clinicianResponse.previous_dose}
                  hasError={hasFieldError('previous_dose')}
                  placeholder="Επίλεξε την προηγούμενη δόση"
                  excludeNoTreatment
                  onChange={(value) => {
                    setClinicianResponse({ ...clinicianResponse, previous_dose: value });
                    setClinicalData({
                      ...clinicalData,
                      previous_dose: value ? parseDoseCombination(value).dailyAverage : null,
                    });
                  }}
                />
                {hasFieldError('previous_dose') && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>{getFieldError('previous_dose')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Αιτία υποθυρεοειδισμού Section */}
            <div className="h-4"></div>
            <div className="space-y-4 mt-6">
              <h3 className="text-sm font-medium border-b pb-2">Αιτία υποθυρεοειδισμού</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'TAI', label: 'Hashimoto' },
                { key: 'thyroidectomy', label: 'Θυρεοειδεκτομή' },
                { key: 'hypothyroidism_before', label: 'Διεγνωσμένος Υποθυρεοειδισμός' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={clinicalData[key as keyof ClinicalData] as boolean}
                    onCheckedChange={(checked) => setClinicalData({...clinicalData, [key]: !!checked})}
                  />
                  <Label htmlFor={key}>{label}</Label>
                </div>
              ))}
            </div>
            </div>

            {/* Ιστορικό /Πλαίσιο Section */}
            <div className="h-4"></div>
            <div className="space-y-4 mt-6">
              <h3 className="text-sm font-medium border-b pb-2">Ιστορικό /Πλαίσιο (που θα μπορούσε να επηρεάζει τη δόση της λεβοθυροξίνης)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'miscarriage_history', label: 'Ιστορικό αποβολών' },
                  { key: 'planning_ivf', label: 'Σχεδιασμός IVF' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={clinicalData[key as keyof ClinicalData] as boolean}
                      onCheckedChange={(checked) => setClinicalData({...clinicalData, [key]: !!checked})}
                    />
                    <Label htmlFor={key}>{label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Αγωγή Section */}
            <div className="h-4"></div>
            <div className="space-y-4 mt-6">
              <h3 className="text-sm font-medium border-b pb-2">Αγωγή (που θα μπορούσε να επηρεάζει την απορρόφηση της λεβοθυροξίνης)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'iron_supplements', label: 'Συμπληρώματα Fe σιδήρου' },
                  { key: 'calcium_supplements', label: 'Συμπληρώματα Ca ασβέστιου' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={clinicalData.medications[key as keyof typeof clinicalData.medications]}
                      onCheckedChange={(checked) => setClinicalData({
                        ...clinicalData,
                        medications: { ...clinicalData.medications, [key]: !!checked }
                      })}
                    />
                    <Label htmlFor={key}>{label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clinician Response */}
          <div className="h-4"></div>
          <div className="space-y-4 border-t pt-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold text-white shrink-0" style={{ backgroundColor: 'var(--brand)' }}>2</span>
              <h3 className="text-xl font-bold" style={{ color: 'var(--brand)' }}>Η σύσταση του Ειδικού Ενδοκρινολόγου</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recommended_dose" className="text-base font-medium">
                Προτεινόμενη Δοσολογία <span className="text-red-500">*</span>
              </Label>
              <DoseCombobox
                value={clinicianResponse.recommended_dose}
                hasError={hasFieldError('recommended_dose')}
                placeholder="Πληκτρολογήστε ή επιλέξτε δοσολογία..."
                onChange={(value) => setClinicianResponse({ ...clinicianResponse, recommended_dose: value })}
              />
              {hasFieldError('recommended_dose') && (
                <div className="flex items-center space-x-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{getFieldError('recommended_dose')}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recommended_dose_alternative" className="text-base font-medium">Εναλλακτική δοσολογική πρόταση</Label>
              <DoseCombobox
                value={clinicianResponse.treatment_recommendation}
                placeholder="Επίλεξε εναλλακτική δοσολογική πρόταση"
                allowClear
                onChange={(value) => setClinicianResponse({ ...clinicianResponse, treatment_recommendation: value })}
              />
            </div>
          </div>

          <Separator className="my-8" />
          
          <div className="flex items-center justify-between pt-6">
            <SaveStateChip
              isDirty={hasUnsavedChanges}
              submitting={submitting}
              lastSavedAt={lastSavedAt}
            />
            
            <Button 
              type="submit" 
              size="lg"
              disabled={submitting || !clinicianResponse.recommended_dose || (clinicalData.on_treatment && !clinicianResponse.previous_dose)}
              className="text-white font-medium px-8 py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#233038' }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {submitPhase === 'saving' && 'Αποθήκευση...'}
                  {submitPhase === 'scoring' && 'Σύσταση αλγορίθμου...'}
                  {submitPhase === 'reconciling' && 'Σύγκριση αποτελεσμάτων...'}
                  {submitPhase === 'idle' && 'Επεξεργασία...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Υποβολή Αξιολόγησης
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
    </div>
  );
};